import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { reportsAPI, feedbackAPI } from '../services/api';
import { useSnackbar } from '../components/Snackbar';
import { renderCorrectedReportHtml, hasCorrections } from '../utils/reportDiff';
import './ReportView.css';

function enforceBoldLines(text) {
  if (!text) return text;
  // Remove patient name identifiers (LASTNAME FIRSTNAME - LETTER pattern)
  text = text.replace(/^[A-Z][A-Z\s]+ - [A-Z](\s?\+\s?[A-Z\s]*)?\s*$/gm, '');
  // Force bold on Highest CTDI and Total DLP lines regardless of AI output
  text = text
    .replace(/^(Highest CTDI:\s*)/gm, '**Highest CTDI:**  ')
    .replace(/^(\*\*Highest CTDI:\*\*\s*)/gm, '**Highest CTDI:**  ')
    .replace(/(Total DLP:\s*)(?!\*)/g, '**Total DLP:**  ')
    .replace(/\*\*Total DLP:\*\*\s*/g, '**Total DLP:**  ');
  // Force all-caps bold on top-level headings
  const headingMap = [
    { pattern: /^\*{0,2}(?:TECHNIQUE|Technique|technique):?\*{0,2}\s*/gm, replacement: '**TECHNIQUE:**  ' },
    { pattern: /^\*{0,2}(?:IMPRESSION|Impression|impression):?\*{0,2}\s*/gm, replacement: '**IMPRESSION:**\n' },
    { pattern: /^\*{0,2}(?:FINDINGS|Findings|findings):?\*{0,2}\s*/gm, replacement: '**FINDINGS:**\n' },
    { pattern: /^\*{0,2}(?:CLINICAL INDICATION|Clinical Indication|clinical indication):?\*{0,2}\s*/gm, replacement: '**CLINICAL INDICATION:**  ' },
    { pattern: /^\*{0,2}(?:COMPARISON|Comparison|comparison):?\*{0,2}\s*/gm, replacement: '**COMPARISON:**  ' },
    { pattern: /^\*{0,2}(?:CONTRAST USED|Contrast Used|contrast used):?\*{0,2}\s*/gm, replacement: '**CONTRAST USED:**  ' },
  ];
  headingMap.forEach(({ pattern, replacement }) => {
    text = text.replace(pattern, replacement);
  });
  return text;
}

function renderReport(text) {
  if (!text) return '';
  text = enforceBoldLines(text);
  // Interleave a zero-width space (U+200B) between each regular space in a
  // run of 2+ spaces. This breaks up the literal "consecutive space
  // character" sequence that Word's paste handler and autocorrect can
  // silently collapse back down to a single space, while remaining
  // completely invisible so it still renders as normal double-spacing.
  const escHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/ {2,}/g, (m) => Array(m.length).fill(' ').join('\u200B'))
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  const lines = text.split('\n');
  let html = ''; let i = 0;
  while (i < lines.length) {
    if (lines[i].trim() === '') {
      while (i < lines.length && lines[i].trim() === '') i++;
      if (i < lines.length) html += '<div style="height:14px"></div>';
    } else {
      html += `<div class="rr-line">${escHtml(lines[i].trim())}</div>`;
      i++;
    }
  }
  return html;
}

async function copyRichText(text) {
  text = enforceBoldLines(text);
 // Same zero-width-space interleaving as renderReport — see that comment
 // for why.
 const preserveSpacing = (s) => s.replace(/ {2,}/g, (m) => Array(m.length).fill(' ').join('\u200B'));
 const escHtml = (s) =>
  preserveSpacing(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  const CONTENT = 'margin:0;padding:0;line-height:1.15;font-size:10pt;font-family:Tahoma;';
  const SPACER  = 'margin:0;padding:0;line-height:1.0;font-size:1pt;font-family:Tahoma;';
  const lines = text.split('\n');
  let body = ''; let i = 0;
  while (i < lines.length) {
    if (lines[i].trim() === '') {
      while (i < lines.length && lines[i].trim() === '') i++;
      if (i < lines.length) body += `<p style="${SPACER}"> </p>`;
    } else {
      body += `<p style="${CONTENT}">${escHtml(lines[i].trim())}</p>`;
      i++;
    }
  }
  const html = `<html><body style="font-family:Tahoma;font-size:10pt;margin:0;padding:0;">${body}</body></html>`;
  const plain = preserveSpacing(text).replace(/\*\*(.*?)\*\*/g, '$1');
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html':  new Blob([html],  { type: 'text/html'  }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      }),
    ]);
  } catch { await navigator.clipboard.writeText(plain); }
}

const ReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const snack = useSnackbar();

  const [report,    setReport]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [edited,    setEdited]    = useState('');
  const [saving,    setSaving]    = useState(false);
  const [copied,    setCopied]    = useState(false);
  const [tab,       setTab]       = useState('final'); 
  const [deletingFb, setDeletingFb] = useState(null); 
  const [selectedVersion, setSelectedVersion] = useState(null); 

  useEffect(() => {
    reportsAPI.getById(id)
      .then(r => {
        setReport(r.data);
        const v = r.data.final_report_versions;
        const latest = v?.length ? v[v.length - 1].version : 1;
        setSelectedVersion(latest);
        const correctedEntry = (r.data.corrected_report_versions || []).find(c => c.version === latest);
        setEdited((correctedEntry?.corrected_report) ?? r.data.corrected_report ?? r.data.final_report ?? '');
      })
      .catch(() => snack('Failed to load report.', 'error'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDeleteFeedback = useCallback(async (createdAt) => {
    setDeletingFb(createdAt);
    try {
      await feedbackAPI.remove(id, createdAt);
      setReport(prev => ({ ...prev, feedback: (prev.feedback || []).filter(f => f.created_at !== createdAt) }));
      snack('Feedback deleted.', 'success');
    } catch { snack('Failed to delete feedback.', 'error'); }
    finally { setDeletingFb(null); }
  }, [id, snack]);

  const versions = report?.final_report_versions?.length
    ? report.final_report_versions
    : (report?.final_report ? [{ version: 1, final_report: report.final_report, created_at: report.updated_at || report.created_at }] : []);
  const latestVersion = versions.length ? versions[versions.length - 1].version : 1;
  const currentVersionEntry = versions.find(v => v.version === selectedVersion);
  const baselineFinalReport = currentVersionEntry?.final_report ?? report?.final_report ?? '';
  const displayedFinalReport = baselineFinalReport;

  const correctedEntry = (report?.corrected_report_versions || []).find(c => c.version === selectedVersion);
  const correctedForVersion = correctedEntry?.corrected_report
    ?? (selectedVersion === latestVersion ? report?.corrected_report : null)
    ?? null;
  const displayedCorrectedReport = isEditing ? edited : (correctedForVersion || baselineFinalReport);
  const correctionsExist = hasCorrections(baselineFinalReport, correctedForVersion);

  const displayedDate = (tab === 'final' && currentVersionEntry)
    ? currentVersionEntry.created_at
    : (report?.updated_at || report?.created_at);

  // Keep the editable draft in sync when switching versions
 useEffect(() => {
    if (!isEditing) setEdited(correctedForVersion || baselineFinalReport);
  }, [selectedVersion, report]);

  const handleCopy = useCallback(async () => {
    const textToCopy = tab === 'corrected' ? displayedCorrectedReport : displayedFinalReport;
    try {
      await copyRichText(textToCopy);
      setCopied(true);
      snack('Copied! Paste into Word.', 'success');
      setTimeout(() => setCopied(false), 2500);
    } catch { snack('Copy failed.', 'error'); }
  }, [tab, displayedCorrectedReport, displayedFinalReport, snack]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await reportsAPI.update(id, { corrected_report: edited, status: 'completed', version: selectedVersion });
      setReport(prev => {
        const others = (prev.corrected_report_versions || []).filter(v => v.version !== selectedVersion);
        const nextVersions = [...others, { version: selectedVersion, corrected_report: edited, created_at: new Date().toISOString() }];
        const isLatest = selectedVersion === latestVersion;
        return {
          ...prev,
          status: 'completed',
          corrected_report_versions: nextVersions,
          corrected_report: isLatest ? edited : prev.corrected_report,
        };
      });
      snack('Correction saved and marked as completed!', 'success');
      setIsEditing(false);
    } catch { snack('Save failed.', 'error'); }
    finally { setSaving(false); }
  }, [id, edited, selectedVersion, latestVersion, snack]);

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  if (loading) return <div className="rv-loading">Loading…</div>;
  if (!report)  return <div className="rv-loading">Report not found.</div>;

  return (
    <div className="rv-page">
      <div className="rv-header">
        <button className="rv-back" onClick={() => navigate(-1)}>
          <ArrowBackIcon sx={{ fontSize: 18 }} /> Back
        </button>

        <div className="rv-meta">
          <div className="rv-meta-item">
            <CalendarTodayIcon sx={{ fontSize: 14, opacity: 0.5 }} />
            <span>{fmt(displayedDate)}</span>
          </div>
          {report.modalities?.name && (
            <div className="rv-meta-item">
              <MedicalServicesIcon sx={{ fontSize: 14, opacity: 0.5 }} />
              <span className="modality-pill">{report.modalities.name}</span>
              {report.body_parts?.name && <span className="rv-bodypart">· {report.body_parts.name}</span>}
            </div>
          )}
          {report.report_type && (
            <span className={`type-pill ${report.report_type}`}>
              {report.report_type === 'hospital' ? 'Hospital' : 'Non Hospital'}
            </span>
          )}
          <span className={`badge badge-${report.status}`}>{report.status}</span>
        </div>

        <div className="rv-actions">
          {(tab === 'final' || tab === 'corrected') && versions.length > 1 && (
            <select
              className="rv-version-select"
              value={selectedVersion ?? latestVersion}
              onChange={e => setSelectedVersion(Number(e.target.value))}
              title="View a previous conversion of this report"
            >
              {versions.map(v => (
                <option key={v.version} value={v.version}>
                  Version {v.version}{v.version === latestVersion ? ' (latest)' : ''}
                </option>
              ))}
            </select>
          )}
          {(tab === 'final' || tab === 'corrected') && (
            <button className="rv-btn" onClick={handleCopy}>
              {copied ? <><CheckIcon sx={{ fontSize: 15 }} /> Copied!</> : <><ContentCopyIcon sx={{ fontSize: 15 }} /> Copy</>}
            </button>
          )}
          {tab === 'corrected' && !isEditing && (
            <button className="rv-btn" onClick={() => setIsEditing(true)}>
              <EditIcon sx={{ fontSize: 15 }} /> Edit
            </button>
          )}
          {tab === 'corrected' && isEditing && (
            <button className="rv-btn" onClick={() => { setIsEditing(false); setEdited(correctedForVersion || baselineFinalReport); }}>
              <CloseIcon sx={{ fontSize: 15 }} /> Cancel
            </button>
          )}
          {tab === 'corrected' && (isEditing || report?.status === 'pending') && (
            <button className="rv-btn rv-btn-save" onClick={handleSave} disabled={saving}>
              <SaveIcon sx={{ fontSize: 15 }} /> {saving ? 'Saving…' : 'Save Correction'}
            </button>
          )}
        </div>
      </div>

      <div className="rv-tabs">
        <button className={`rv-tab ${tab === 'final' ? 'active' : ''}`} onClick={() => { setTab('final'); setIsEditing(false); }}>
          Final Report
        </button>
        <button className={`rv-tab ${tab === 'corrected' ? 'active' : ''}`} onClick={() => { setTab('corrected'); setIsEditing(false); }}>
          Corrected Report {correctionsExist && <span className="corrected-dot" title="Has corrections">●</span>}
        </button>
        <button className={`rv-tab ${tab === 'raw' ? 'active' : ''}`} onClick={() => { setTab('raw'); setIsEditing(false); }}>
          Raw Report
        </button>
        <button className={`rv-tab ${tab === 'feedback' ? 'active' : ''}`} onClick={() => { setTab('feedback'); setIsEditing(false); }}>
          Feedback {report.feedback?.length > 0 && `(${report.feedback.length})`}
        </button>
      </div>

      <div className="rv-body">
        {tab === 'final' ? (
          <div
            className="rv-rendered"
            dangerouslySetInnerHTML={{ __html: renderReport(displayedFinalReport) || '<em>No final report yet.</em>' }}
          />
        ) : tab === 'corrected' ? (
          isEditing ? (
            <textarea
              className="rv-textarea"
              value={edited}
              onChange={e => setEdited(e.target.value)}
              autoFocus
            />
          ) : (
            <>
              {!correctionsExist && (
                <p className="rv-no-corrections">No corrections made yet — this matches the Final Report.</p>
              )}
              <div
                className="rv-rendered"
                dangerouslySetInnerHTML={{ __html: renderCorrectedReportHtml(baselineFinalReport, displayedCorrectedReport) }}
              />
            </>
          )
        ) : tab === 'raw' ? (
          <pre className="rv-raw">{report.raw_report || '—'}</pre>
        ) : (
          <div className="rv-feedback-list">
            {(!report.feedback || report.feedback.length === 0) ? (
              <p className="rv-feedback-empty">No feedback has been submitted for this report yet.</p>
            ) : (
              report.feedback.map((f) => (
                <div key={f.created_at} className="rv-feedback-item">
                  <button
                    className="rv-feedback-delete"
                    onClick={() => handleDeleteFeedback(f.created_at)}
                    disabled={deletingFb === f.created_at}
                    title="Delete this feedback"
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                  <div className="rv-feedback-date">{fmt(f.created_at)}</div>
                  <p className="rv-feedback-msg">{f.message}</p>
                  {f.files?.length > 0 && (
                    <div className="rv-feedback-files">
                      {f.files.map((file, fi) => {
                        const isImage = file.type?.startsWith('image/');
                        return (
                          <a key={fi} href={file.url} target="_blank" rel="noopener noreferrer" className="rv-feedback-file">
                            {isImage
                              ? <img src={file.url} alt={file.name} className="rv-feedback-thumb" />
                              : <span className="rv-feedback-file-icon"><InsertDriveFileIcon sx={{ fontSize: 16 }} /></span>}
                            <span className="rv-feedback-file-name">{file.name}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportView;